import Lecture from 'components/pages/lecture/lecture/Lecture';

import Head from 'next/head';

import { dehydrate, QueryClient } from 'react-query';

import { queryClientConfig } from '@core/configs/queryClientConfig';

import { LectureService, LectureComments } from 'services/core';

import {
	CONTENT_COMMENTS_PAGE_SIZE,
	contentCommentsQueryKey
} from 'constants/content-comments.const';

import { setSsrTokens } from 'utils/authentication';

import { NextSeo } from 'next-seo';
import { getLecturesSeoProps, getLecturesJsonLd } from 'router/seoprops';

const Lectures = ({ jsonLd, ...props }) => {
	const seoProps = getLecturesSeoProps({ ...props });
	return (
		<>
			<NextSeo {...seoProps} />
			{jsonLd && (
				<Head>
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
					/>
				</Head>
			)}
			<Lecture />
		</>
	);
};

export async function getServerSideProps(context) {
	setSsrTokens(context);
	const queryClient = new QueryClient(queryClientConfig);
	const { req } = context;
	const userIp = req.headers['x-forwarded-for'];
	const lectureID = context?.params?.slug[0];
	try {
		const response = await LectureService(lectureID, userIp);
		await queryClient.prefetchQuery(['lecture', lectureID], () => ({ data: response.data }));
		let newQueryParams = '';
		const { slug, ...queryParams } = context.query;
		if (queryParams && Object.keys(queryParams).length > 0) {
			newQueryParams = `?${new URLSearchParams(queryParams).toString()}`;
		}
		if (!context?.params?.slug[1]) {
			context.res.setHeader(
				'Location',
				`/courses/${context?.params?.slug[0]}/${response.data.slug}${newQueryParams}`
			);
			context.res.statusCode = 302;
			context.res.end();
			return { props: {} };
		}

		// The reviews are rendered server side so crawlers see them in the HTML.
		// A failure here must not take the whole page down, so it degrades back
		// to the client fetching them on mount.
		let reviews = [];
		try {
			const commentsResponse = await LectureComments({
				id: lectureID,
				page_size: CONTENT_COMMENTS_PAGE_SIZE,
				page: 1
			});
			reviews = commentsResponse.data?.results || [];
			// Written straight into the cache rather than prefetched: the
			// infinite query helper records a pageParam of undefined for the
			// first page, which getServerSideProps cannot serialize.
			queryClient.setQueryData(contentCommentsQueryKey('lecture', lectureID), {
				pages: [{ data: commentsResponse.data }],
				pageParams: [1]
			});
		} catch (e) {
			reviews = [];
		}

		return {
			props: {
				dehydratedState: dehydrate(queryClient),
				id: lectureID,
				title: response.data.title,
				description: response.data.meta_description || response.data.short_introduction,
				slug: response.data.slug,
				jsonLd: getLecturesJsonLd({
					lecture: response.data,
					reviews,
					id: lectureID,
					slug: response.data.slug
				})
			}
		};
	} catch (e) {
		if (e.response.status === 404)
			return {
				redirect: {
					permanent: false,
					destination: "/404"
				}
			}
		else
			return {}
	}
}

export default Lectures;
